import { decodeJWT, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

export const SSO_HASH_KEY = 'tw_sso';
const SSO_PENDING_KEY = 'tw_sso_pending';

export type SsoHandoffPayload = {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  username: string;
};

export function base64UrlEncode(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLen);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function resolveUsername(accessToken: string, idToken: string, fallback: string): string {
  try {
    const accessPayload = decodeJWT(accessToken).payload;
    const idPayload = decodeJWT(idToken).payload;
    return String(
      accessPayload.username ??
        idPayload['cognito:username'] ??
        idPayload.sub ??
        fallback,
    );
  } catch {
    return fallback;
  }
}

export async function buildSsoHandoffPayload(): Promise<SsoHandoffPayload | null> {
  const session = await fetchAuthSession({ forceRefresh: true });
  const tokens = session.tokens;
  if (!tokens?.accessToken || !tokens?.idToken) {
    return null;
  }

  const accessToken = tokens.accessToken.toString();
  const idToken = tokens.idToken.toString();
  let refreshToken = '';

  try {
    const stored = await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens();
    refreshToken = stored?.refreshToken ?? '';
  } catch {
    // refresh token is optional for cross-app landing
  }

  let username = '';
  try {
    username = (await getCurrentUser()).username;
  } catch {
    username = resolveUsername(accessToken, idToken, '');
  }

  username = resolveUsername(accessToken, idToken, username);
  if (!username) {
    return null;
  }

  return {
    accessToken,
    idToken,
    refreshToken,
    username,
  };
}

/** Append Cognito session to target URL hash (not sent to servers). */
export function appendSsoHashToUrl(target: URL, payload: SsoHandoffPayload | null): void {
  if (payload) {
    target.hash = `${SSO_HASH_KEY}=${base64UrlEncode(JSON.stringify(payload))}`;
  } else {
    target.searchParams.set('from', 'sso');
  }
}

export async function redirectWithSsoHandoff(baseUrl: string, path = '/'): Promise<void> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const target = new URL(`${baseUrl.replace(/\/+$/, '')}${normalizedPath}`);
  const payload = await buildSsoHandoffPayload();
  appendSsoHashToUrl(target, payload);
  window.location.assign(target.toString());
}

function parseSsoHash(): SsoHandoffPayload | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash.startsWith(`${SSO_HASH_KEY}=`)) return null;
  try {
    const json = base64UrlDecode(hash.slice(SSO_HASH_KEY.length + 1));
    const payload = JSON.parse(json) as SsoHandoffPayload;
    if (!payload.accessToken || !payload.idToken || !payload.username) return null;
    return payload;
  } catch {
    return null;
  }
}

function readPendingPayload(): SsoHandoffPayload | null {
  try {
    const raw = sessionStorage.getItem(SSO_PENDING_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw) as SsoHandoffPayload;
    if (!payload.accessToken || !payload.idToken || !payload.username) return null;
    return payload;
  } catch {
    return null;
  }
}

function clearSsoHashFromUrl(): void {
  if (typeof window === 'undefined' || !window.location.hash.includes(SSO_HASH_KEY)) return;
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
}

async function persistHandoffTokens(payload: SsoHandoffPayload): Promise<void> {
  const accessToken = decodeJWT(payload.accessToken);
  const idToken = decodeJWT(payload.idToken);
  const issuedAtMs = (accessToken.payload.iat || 0) * 1000;
  const clockDrift = issuedAtMs > 0 ? issuedAtMs - Date.now() : 0;
  const username =
    payload.username ||
    String(idToken.payload['cognito:username'] ?? accessToken.payload.username ?? '');

  await cognitoUserPoolsTokenProvider.tokenOrchestrator.setTokens({
    tokens: {
      accessToken,
      idToken,
      refreshToken: payload.refreshToken || undefined,
      clockDrift,
      username,
    },
  });
}

/**
 * Apply SSO tokens once during app bootstrap (before React mounts).
 * Uses sessionStorage backup so React StrictMode remounts cannot lose the handoff.
 */
export async function bootstrapSsoTokenHandoff(): Promise<boolean> {
  let payload = parseSsoHash();

  if (payload) {
    try {
      sessionStorage.setItem(SSO_PENDING_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
    clearSsoHashFromUrl();
  } else {
    payload = readPendingPayload();
  }

  if (!payload) return false;

  await persistHandoffTokens(payload);

  try {
    sessionStorage.removeItem(SSO_PENDING_KEY);
  } catch {
    // ignore
  }

  return true;
}
